import React from 'react';
import { Cell, flexRender, Table } from '@tanstack/react-table';
import { DataCell } from '@/components/ui/DataCell/DataCell';
import ValidationStatusCell from '@/components/ui/DataCell/ValidationStatusCell';
import VersionInfoCell from '@/components/ui/DataCell/VersionInfoCell';
import VersionImportInfoCell from '@/components/ui/DataCell/VersionImportInfoCell';
import { DataTableColumnMeta } from './types';

/* eslint-disable @typescript-eslint/no-explicit-any -- DataTable cell rendering is intentionally generic across many row shapes. */

interface DataTableCellRendererProps<TData> {
  cell: Cell<TData, unknown>;
  table: Table<TData>;
}

/**
 * Renders table cell with support for automatic rendering based on meta.cell.variant
 * Falls back to standard TanStack Table cell rendering if variant is not specified
 *
 * Priority:
 * 1. Skeleton data: use cell function directly (for skeleton columns)
 * 2. Variant-based rendering (if variant is specified in meta.cell.variant)
 * 3. Custom cell function (backward compatibility)
 * 4. Fallback to text rendering
 */
export function DataTableCellRenderer<TData>({
  cell,
  table,
}: DataTableCellRendererProps<TData>) {
  void table;
  const meta = cell.column.columnDef.meta as DataTableColumnMeta | undefined;
  const cellMeta = meta?.cell;
  const row = cell.row.original as any;

  // Check if this is skeleton data (has __skeleton_id)
  const isSkeleton = row?.__skeleton_id !== undefined;

  // Priority 1: If this is skeleton data, use the cell function directly (skeleton cells)
  // This handles skeleton columns that have their cell function replaced with DataTableSkeletonCell
  if (isSkeleton) {
    // If column has a custom cell function (should be skeleton cell), use it
    if (
      cell.column.columnDef.cell &&
      typeof cell.column.columnDef.cell === 'function'
    ) {
      return flexRender(cell.column.columnDef.cell, cell.getContext());
    }
    // Otherwise, fall through to variant-based rendering which will use skeleton
  }

  // Priority 2: If variant is specified, use automatic rendering (variant has priority over custom cell)
  if (cellMeta?.variant) {
    const value = cell.getValue();
    const row = cell.row.original;
    const accessorKey = cell.column.id;

    // Handle custom render function
    if (cellMeta.variant === 'custom' && cellMeta.render) {
      const customResult = cellMeta.render(value, row);
      // If custom render returns null, fall back to fileName variant if accessorKey suggests it
      if (customResult === null && accessorKey === 'file.name') {
        // Fall through to fileName variant handling below
      } else if (customResult !== null && customResult !== undefined) {
        return <>{customResult}</>;
      }
    }

    // Extract data based on variant type and accessorKey
    const cellProps: any = {
      value,
      format: cellMeta.format,
      variant: cellMeta.badgeVariant,
      size: cellMeta.size,
      color: cellMeta.color,
      actions: cellMeta.actions,
      src: cellMeta.src,
      alt: cellMeta.alt,
      icon: cellMeta.icon,
      showEmail: cellMeta.showEmail,
      precision: cellMeta.precision,
      showIcon: cellMeta.showIcon,
      truncate: cellMeta.truncate,
      endSlot: cellMeta.endSlot,
      showGroup: cellMeta.showGroup,
      showTime: cellMeta.showTime,
      relative: cellMeta.relative,
      serverTimezone: cellMeta.serverTimezone,
      maxVisible: cellMeta.maxVisible,
      chipSize: cellMeta.chipSize,
      chipColor: cellMeta.chipColor,
      chipVariant: cellMeta.chipVariant,
      startIcon: cellMeta.startIcon,
      endIcon: cellMeta.endIcon,
      highContrast: cellMeta.highContrast,
      actionMap: cellMeta.actionMap,
      messageType: cellMeta.messageType,
      messageMaxWidth: cellMeta.messageMaxWidth,
      entityType:
        typeof cellMeta.entityType === 'function'
          ? cellMeta.entityType(row)
          : cellMeta.entityType,
      status:
        typeof cellMeta.status === 'function'
          ? cellMeta.status(row)
          : cellMeta.status,
      label:
        typeof cellMeta.label === 'function'
          ? cellMeta.label(row)
          : cellMeta.label,
      className:
        typeof cellMeta.className === 'function'
          ? cellMeta.className(row, cell.row.index)
          : cellMeta.className,
      // Support render function for all variants (not just custom)
      render: cellMeta.render
        ? () => {
            const renderResult = cellMeta.render!(value, row);
            // If render returns undefined/null, use default rendering
            return renderResult !== undefined && renderResult !== null
              ? renderResult
              : undefined;
          }
        : undefined,
    };

    // Type-specific data extraction based on variant and accessorKey
    switch (cellMeta.variant) {
      case 'user':
        // For user cell, value is the user object from accessorKey 'user'
        // Ensure we have a valid user object
        if (value && typeof value === 'object') {
          cellProps.user = value as any;
        } else {
          // Fallback: try to get from row
          cellProps.user = (row as any).user;
        }
        break;

      case 'fileSize':
        // For fileSize cell, value is the size in bytes
        if (typeof value === 'number') {
          cellProps.bytes = value;
        } else {
          // Fallback: try to get from row.size
          cellProps.bytes = (row as any).size;
        }
        break;

      case 'fileName':
        // For fileName cell, value is the filename, but we need mimeType too
        if (typeof value === 'string') {
          cellProps.filename = value;
        } else {
          // Fallback: try to get from row.filename
          cellProps.filename = (row as any).filename;
        }
        // Try to get mimeType from row (support both ProcessedFile and standard formats)
        cellProps.mimeType =
          (row as any).file?.type ||
          (row as any).content_type ||
          (row as any).mimeType;
        // Handle endSlot as function or ReactNode
        if (cellMeta.endSlot) {
          cellProps.endSlot =
            typeof cellMeta.endSlot === 'function'
              ? cellMeta.endSlot(row, cell.row.index)
              : cellMeta.endSlot;
        }
        break;

      case 'fileFormat':
        // For fileFormat cell, value should be the extension (without dot)
        // This is typically extracted via accessorFn
        if (typeof value === 'string' && value.length > 0) {
          // Value is the extension (e.g., "pdf", "csv")
          cellProps.extension = value;
        } else {
          // Fallback: extract from filename in row
          const filename = (row as any).filename;
          if (filename && typeof filename === 'string') {
            const lastDot = filename.lastIndexOf('.');
            cellProps.extension =
              lastDot > 0 ? filename.substring(lastDot + 1) : undefined;
          } else {
            cellProps.extension = undefined;
          }
        }
        break;

      case 'mimeType':
        // For mimeType cell, value is the content_type
        if (typeof value === 'string') {
          cellProps.mimeType = value;
        } else {
          // Fallback: try to get from row
          cellProps.mimeType =
            (row as any).content_type || (row as any).mimeType;
        }
        break;

      case 'datetime':
      case 'time':
        // For datetime/time cells, value is the date string
        // Default to relative time if not explicitly set
        if (cellProps.relative === undefined) {
          cellProps.relative = true;
        }
        cellProps.value = value;
        break;
      case 'date':
        // For date cells, value is the date string
        cellProps.value = value;
        break;

      case 'count':
        // For count cell, value is the number
        cellProps.value = value;
        break;

      case 'chips':
        // For chips cell, value should be an array of strings
        cellProps.value = value;
        break;

      case 'action':
        // For action cell, value is the action string
        cellProps.value = value;
        break;

      case 'message':
        // For message cell, value is the message string
        cellProps.value = value;
        break;

      case 'badge':
        // For badge cell, support icons
        cellProps.value = value;
        break;

      case 'status':
        // For status cell, value is the status string
        cellProps.value = value;
        // entityType, status, and label are already in cellProps from above
        break;

      case 'validationStatus':
        // For validation status cell, we need the full file object
        // Pass the row as file prop
        return (
          <ValidationStatusCell
            value={value}
            file={row as any}
            onStatusChange={cellMeta.onStatusChange}
            render={cellProps.render}
          />
        );

      case 'versionInfo':
        // For version info cell, we need the full file object
        // Pass the row as file prop
        return (
          <VersionInfoCell
            value={value}
            file={row as any}
            render={cellProps.render}
          />
        );

      case 'versionImportInfo':
        // For version import info cell, we need the full version object
        // Pass the row as version prop
        return (
          <VersionImportInfoCell
            value={value}
            version={row as any}
            render={cellProps.render}
          />
        );

      default:
        // For other types (text, number, boolean, badge, etc.), just pass the value
        cellProps.value = value;
    }

    // Use DataCell for standard variants
    return <DataCell type={cellMeta.variant as any} {...cellProps} />;
  }

  // Priority 3: Check if custom cell function returns a value (backward compatibility)
  // Only used if variant is not specified
  if (
    cell.column.columnDef.cell &&
    typeof cell.column.columnDef.cell === 'function'
  ) {
    const customCellResult = flexRender(
      cell.column.columnDef.cell,
      cell.getContext()
    );
    // If custom cell returns a valid value, use it
    if (customCellResult !== null && customCellResult !== undefined) {
      return customCellResult;
    }
  }

  // Priority 4: Fallback - render value as text
  const value = cell.getValue();
  return <DataCell type="text" value={value} />;
}
